import { convexQuery } from "@convex-dev/react-query";
import type { Id } from "@convex/_generated/dataModel";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useForm } from "react-hook-form";

import {
  categoriesGetAll,
  itemsAssignToCategories,
  itemsGetAll,
} from "~/api-refs";
import { Button } from "~/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "~/components/ui/form";
import { MultiCombobox } from "~/components/ui/multi-combobox";
import { convexClient } from "~/integrations/convex/provider";
import { toConvexTableId } from "~/lib/convex-table-id";

import type { ModalTypes } from "./types";

interface ExistingItemsFormData {
  items: string[];
  categories: string[];
}

export const AddExistingItemForm = ({
  setModalType,
  onFinished,
}: {
  onFinished: () => void;
  setModalType: (type: ModalTypes | null) => void;
}) => {
  const { data: itemsFromMerchantResult } = useQuery(
    convexQuery(itemsGetAll, {
      includeCategoryRelations: true,
      includeMenus: true,
      includeModifierGroupsCount: true,
      paginationOpts: { numItems: 100, cursor: null },
    })
  );
  const itemsFromMerchant = itemsFromMerchantResult?.page;

  const createItemsMutation = useMutation({
    mutationFn: (data: {
      items: Id<"items">[];
      categories: Id<"categories">[];
    }) => {
      return convexClient.mutation(itemsAssignToCategories, data);
    },
  });

  const { data: categoriesResult } = useQuery(
    convexQuery(categoriesGetAll, {
      includeItemsCount: true,
      includeMenus: true,
      paginationOpts: { numItems: 100, cursor: null },
    })
  );
  const categories = categoriesResult?.page;
  const form = useForm<ExistingItemsFormData>({
    defaultValues: {
      items: [],
      categories: [],
    },
  });

  const onSubmit = async (data: ExistingItemsFormData) => {
    try {
      await createItemsMutation.mutateAsync({
        items: data.items.map((id) => toConvexTableId("items", id)),
        categories: data.categories.map((id) =>
          toConvexTableId("categories", id)
        ),
      });
      onFinished();
    } catch {
      form.setError("items", {
        message: "There was an error adding these items to selected categories",
      });
    }
  };

  return (
    <div>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <FormField
            control={form.control}
            name="items"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Choose items to add and share</FormLabel>
                <FormControl>
                  <MultiCombobox
                    groups={["default"]}
                    items={{
                      default: (itemsFromMerchant ?? []).map((item) => ({
                        label: item.name,
                        value: item._id,
                        component: (
                          <div className="w-full space-y-1">
                            <div className="flex w-full items-center justify-between gap-1">
                              <p className="text-foreground">{item.name}</p>
                              {item.modifierGroupsCount !== "0" && (
                                <p className="caption bg-muted text-foreground rounded-full px-2 py-1">
                                  {item.modifierGroupsCount} mod. group
                                  {item.modifierGroupsCount !== "1" && "s"}
                                </p>
                              )}
                            </div>
                            <div className="flex items-center gap-1">
                              {item.categoryRelations.map(
                                (categoryRelation) => (
                                  <div
                                    className="caption bg-category-bg text-category rounded-full px-2 py-1"
                                    key={categoryRelation.category.id}
                                  >
                                    {categoryRelation.category.name}
                                  </div>
                                )
                              )}
                            </div>
                            <div className="flex items-center gap-1">
                              {item.categoryRelations
                                .flatMap((cr) => cr.category.menuRelations)
                                .filter(
                                  (
                                    menuRelation
                                  ): menuRelation is NonNullable<
                                    typeof menuRelation
                                  > => Boolean(menuRelation)
                                )
                                .map((menuRelation) => (
                                  <div
                                    className="caption bg-menu-bg text-menu rounded-full px-2 py-1"
                                    key={menuRelation.menu.id}
                                  >
                                    {menuRelation.menu.name}
                                  </div>
                                ))}
                            </div>
                          </div>
                        ),
                      })),
                    }}
                    onValueChange={(d) => field.onChange(d.default)}
                    placeholder="Select items..."
                    values={{
                      default: field.value ?? [],
                    }}
                    {...field}
                  />
                </FormControl>
                <FormMessage className="mb-4" />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="categories"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="mt-4">Add to menu category</FormLabel>
                <FormControl>
                  <MultiCombobox
                    groups={["default"]}
                    items={{
                      default: (categories ?? []).map((category) => ({
                        value: category.id,
                        label: category.name,
                        component: (
                          <div className="w-full space-y-1">
                            <div className="flex w-full items-baseline gap-1">
                              <p className="text-foreground">{category.name}</p>
                              {category.itemCount !== 0 && (
                                <p className="body-sm text-muted-foreground">
                                  {category.itemCount} item
                                  {category.itemCount !== 1 && "s"}
                                </p>
                              )}
                            </div>
                            <div className="flex items-center gap-1">
                              {category.menuRelations?.map((menuRelation) => (
                                <div
                                  className="caption bg-menu-bg text-menu rounded-full px-2 py-1"
                                  key={menuRelation.menu.id}
                                >
                                  {menuRelation.menu.name}
                                </div>
                              ))}
                            </div>
                          </div>
                        ),
                      })),
                    }}
                    onValueChange={(d) => field.onChange(d.default)}
                    values={{
                      default: field.value ?? [],
                    }}
                  />
                </FormControl>
                <FormMessage className="mb-4" />
              </FormItem>
            )}
          />
          <div className="mt-4 flex w-full space-x-2">
            <Button
              className="flex-1"
              onClick={() => {
                form.reset();
                setModalType(null);
              }}
              type="button"
              variant="secondary"
            >
              Back
            </Button>
            <Button
              className="flex-1"
              disabled={createItemsMutation.isPending}
              type="submit"
              variant="default"
            >
              Add Items
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
};
